import React, { useRef } from 'react';
import { Card, CardContent, Typography, Box, Button, IconButton, Stack, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AttachFile, Delete, CloudUpload, Image as ImageIcon, PictureAsPdf } from '@mui/icons-material';

interface Attachment {
  id: string;
  name: string;
  url: string;
  cdnUrl?: string;
  type: string;
  size: number;
}

interface AttachmentsCardProps {
  attachments: Attachment[];
  visitId: string;
  onUpload: (file: File) => Promise<void>;
  onDelete: (attachmentId: string) => Promise<void>;
  disabled?: boolean;
  uploading?: boolean;
}

const AttachmentsCard: React.FC<AttachmentsCardProps> = React.memo(({ 
  attachments, 
  visitId,
  onUpload, 
  onDelete, 
  disabled = false,
  uploading = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      await onUpload(files[i]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <ImageIcon />;
    if (type === 'application/pdf') return <PictureAsPdf />;
    return <AttachFile />;
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachFile sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="h6">Atașamente</Typography>
          </Box>
          <Button
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading}
            size="small"
            variant="outlined"
          >
            {uploading ? 'Se încarcă...' : 'Încarcă fișier'}
          </Button>
        </Box>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          disabled={disabled || uploading}
        />

        {/* Attachments list */}
        {attachments.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Nu există atașamente. Încărcați poze, PDF-uri, rezultate laborator.
          </Typography>
        )}

        <Grid container spacing={2}>
          {attachments.map((attachment) => (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={attachment.id}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  position: 'relative',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
              >
                {/* Preview */}
                <Box
                  sx={{
                    width: '100%',
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    mb: 1,
                    overflow: 'hidden'
                  }}
                >
                  {attachment.type.startsWith('image/') ? (
                    <img
                      src={attachment.cdnUrl || attachment.url}
                      alt={attachment.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                      {getFileIcon(attachment.type)}
                      <Typography variant="caption" display="block">
                        {attachment.type.split('/')[1]?.toUpperCase()}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Info */}
                <Typography variant="caption" display="block" noWrap title={attachment.name}>
                  {attachment.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatFileSize(attachment.size)}
                </Typography>

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Button
                    size="small"
                    component="a"
                    href={attachment.cdnUrl || attachment.url}
                    target="_blank"
                    rel="noopener"
                  >
                    Vezi
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(attachment.id)}
                    disabled={disabled}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
});

AttachmentsCard.displayName = 'AttachmentsCard';

export default AttachmentsCard;

